import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials, businessInfo } from '../data/mock';
import SEO from '../components/SEO';

const Testimonials = () => {
  const getCardStyle = (index) => {
    const styles = [
      'bg-white',
      'bg-teal-50',
      'bg-slate-50',
    ];
    return styles[index % styles.length];
  };

  return (
    <>
      <SEO 
        title="Client Testimonials - Real Stories of Healing & Growth"
        description="Read authentic testimonials from clients who have experienced healing and growth through therapy at Calling The Deep Counseling Services. Discover how compassionate, evidence-based therapy can help you too."
        keywords="therapy testimonials Lebanon PA, client reviews marriage therapist, family therapy success stories, counseling reviews Pennsylvania"
        url="/testimonials"
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-teal-50 via-white to-slate-50">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute bottom-0 right-0 w-1/3 h-2/3 bg-gradient-to-tl from-teal-100/40 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <img 
              src={businessInfo.logo} 
              alt={businessInfo.name}
              className="h-32 w-auto mx-auto mb-6"
            />
            <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Testimonials</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mt-2 mb-6">
              Stories of Healing & Growth
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Read what our clients have to say about their experiences working with Calling The Deep Counseling Services.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Privacy Notice */}
          <div className="mb-12 p-4 bg-slate-100 rounded-xl text-center">
            <p className="text-sm text-slate-600">
              <span className="font-medium">Privacy Notice:</span> All names have been changed to protect privacy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={testimonial.id} 
                className={`border-0 shadow-lg hover:shadow-xl transition-all duration-300 ${getCardStyle(index)}`}
              >
                <CardContent className="p-8">
                  {/* Quote Icon */}
                  <div className="mb-6">
                    <Quote className="h-10 w-10 text-teal-400" />
                  </div>
                  
                  {/* Stars */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-teal-500 text-teal-500" />
                    ))}
                  </div>
                  
                  {/* Quote Text */}
                  <blockquote className="text-slate-700 leading-relaxed mb-6">
                    "{testimonial.quote}"
                  </blockquote>
                  
                  {/* Author */}
                  <div className="border-t border-slate-200 pt-4">
                    <p className="font-semibold text-slate-800">{testimonial.author}</p>
                    <p className="text-sm text-teal-600">{testimonial.service}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Quote */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <Quote className="h-16 w-16 mx-auto mb-8 opacity-50" />
            <blockquote className="text-2xl sm:text-3xl font-light leading-relaxed mb-8">
              "The journey of a thousand miles begins with a single step. You've already taken that step by considering therapy."
            </blockquote>
            <div className="w-16 h-0.5 bg-white/30 mx-auto" />
          </div>
        </div>
      </section>

      {/* Why Clients Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Why Clients Choose Us
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Safe & Confidential",
                description: "Your privacy is paramount. Everything shared in sessions stays strictly confidential."
              },
              {
                title: "Personalized Care",
                description: "No cookie-cutter approaches. Your treatment plan is tailored specifically to your needs."
              },
              {
                title: "Lasting Results",
                description: "We focus on sustainable change and equip you with tools for long-term success."
              }
            ].map((item, index) => (
              <div key={index} className="text-center p-8">
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-teal-600">{index + 1}</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Your Story Matters
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Every journey is unique. We're here to listen, support, and guide you toward your own story of healing and growth.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 text-lg">
              Begin Your Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Testimonials;
